import { trackError, trackRumAction } from '@cartwheel/datadog'
import type { Call, Device } from '@twilio/voice-sdk'
import { useCallback, useRef, useState } from 'react'

export type CallStep = 'confirm' | 'connecting' | 'ringing' | 'in_call' | 'ended'

export interface CallEndedState {
  reason: 'completed' | 'failed' | 'canceled' | 'no_answer'
  errorMessage?: string
}

export interface QualityAlert {
  severity: 'no_audio' | 'degraded'
  message: string
}

export interface UseTwilioDeviceReturn {
  step: CallStep
  isMuted: boolean
  ended: CallEndedState | null
  isStarting: boolean
  qualityAlert: QualityAlert | null
  startCall: (token: string, callLogId: string, targetNumber: string) => Promise<void>
  mute: () => void
  hangUp: () => void
  sendDigit: (digit: string) => void
  cleanup: () => void
  reset: () => void
}

const NO_AUDIO_WARNINGS: Record<string, string> = {
  'low-bytes-received': 'You are not receiving any audio. Your network may be blocking the call.',
  'low-bytes-sent':
    'No audio is leaving your browser. Check that the right microphone is selected.',
  'constant-audio-input-level':
    'We cannot hear your microphone. Check that it is not muted on your computer.',
  'ice-connectivity-lost': 'The connection to the call was lost. Audio may cut out.',
}

const DEGRADED_WARNINGS: Record<string, string> = {
  'high-packet-loss': 'Your connection is unstable, so audio may sound choppy.',
  'high-packets-lost-fraction': 'Your connection is unstable, so audio may sound choppy.',
  'high-jitter': 'Your connection is unstable, so audio may sound choppy.',
  'low-mos': 'Call quality is poor. Consider hanging up and calling back.',
  'high-rtt': 'There may be a noticeable delay on this call.',
}

function alertFor(warnings: Set<string>): QualityAlert | null {
  for (const name of warnings) {
    const message = NO_AUDIO_WARNINGS[name]
    if (message) return { severity: 'no_audio', message }
  }
  for (const name of warnings) {
    const message = DEGRADED_WARNINGS[name]
    if (message) return { severity: 'degraded', message }
  }
  return null
}

async function assertMicrophoneAvailable(): Promise<void> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  for (const track of stream.getTracks()) track.stop()
}

export function useTwilioDevice(): UseTwilioDeviceReturn {
  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const warningsRef = useRef<Set<string>>(new Set())
  const acceptedRef = useRef(false)
  const hungUpLocallyRef = useRef(false)

  const [step, setStep] = useState<CallStep>('confirm')
  const [isMuted, setIsMuted] = useState(false)
  const [ended, setEnded] = useState<CallEndedState | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [qualityAlert, setQualityAlert] = useState<QualityAlert | null>(null)

  const cleanup = useCallback(() => {
    if (callRef.current) {
      callRef.current.removeAllListeners()
      callRef.current = null
    }
    if (deviceRef.current) {
      deviceRef.current.destroy()
      deviceRef.current = null
    }
    warningsRef.current = new Set()
  }, [])

  const reset = useCallback(() => {
    acceptedRef.current = false
    hungUpLocallyRef.current = false
    cleanup()
    setStep('confirm')
    setIsMuted(false)
    setEnded(null)
    setIsStarting(false)
    setQualityAlert(null)
  }, [cleanup])

  const startCall = useCallback(
    async (token: string, callLogId: string, targetNumber: string) => {
      setIsStarting(true)
      setQualityAlert(null)
      acceptedRef.current = false
      hungUpLocallyRef.current = false

      const fail = (error: unknown, errorMessage: string) => {
        trackError(error, { context: 'twilio_dialer.call_failed', callLogId })
        setEnded({ reason: 'failed', errorMessage })
        setStep('ended')
        setIsStarting(false)
        cleanup()
      }

      try {
        await assertMicrophoneAvailable()
      } catch {
        trackRumAction('twilio_dialer_microphone_denied', { callLogId })
        setEnded({
          reason: 'failed',
          errorMessage:
            'We could not access your microphone. Allow microphone access and try again.',
        })
        setStep('ended')
        setIsStarting(false)
        cleanup()
        return
      }

      const { Call: TwilioCall, Device: TwilioDevice } = await import('@twilio/voice-sdk')

      // Opus carries forward error correction, so lossy networks degrade to choppy
      // audio rather than silence. The SDK default can settle on PCMU, which has none.
      const device = new TwilioDevice(token, {
        logLevel: 'warn',
        codecPreferences: [TwilioCall.Codec.Opus, TwilioCall.Codec.PCMU],
      })
      deviceRef.current = device

      device.on('error', (err: Error) => {
        trackError(err, { context: 'twilio_dialer.device_error', callLogId })
        if (callRef.current) return
        fail(err, err.message)
      })

      let call: Call
      try {
        call = await device.connect({
          params: {
            call_log_id: callLogId,
            To: targetNumber,
          },
        })
      } catch (err) {
        fail(err, err instanceof Error ? err.message : 'The call could not be started.')
        return
      }

      callRef.current = call
      setStep('connecting')
      setIsStarting(false)

      // With answerOnBridge on the <Dial>, accept fires only once the target
      // actually picks up, so these two steps are distinct states.
      call.on('ringing', () => setStep('ringing'))
      call.on('accept', () => {
        acceptedRef.current = true
        setStep('in_call')
      })

      call.on('warning', (warningName: string) => {
        warningsRef.current.add(warningName)
        setQualityAlert(alertFor(warningsRef.current))
        trackRumAction('twilio_dialer_call_warning', { warningName, callLogId })
      })

      call.on('warning-cleared', (warningName: string) => {
        warningsRef.current.delete(warningName)
        setQualityAlert(alertFor(warningsRef.current))
      })

      call.on('mute', (_: boolean, mutedCall: Call) => {
        setIsMuted(mutedCall.isMuted())
      })

      call.on('disconnect', () => {
        const reason = acceptedRef.current
          ? 'completed'
          : hungUpLocallyRef.current
            ? 'canceled'
            : 'no_answer'
        setEnded({ reason })
        setStep('ended')
        cleanup()
      })

      call.on('cancel', () => {
        setEnded({ reason: 'canceled' })
        setStep('ended')
        cleanup()
      })

      call.on('error', (err: Error) => {
        fail(err, err.message)
      })
    },
    [cleanup]
  )

  const mute = useCallback(() => {
    const call = callRef.current
    if (!call) return
    const next = !isMuted
    call.mute(next)
    setIsMuted(next)
  }, [isMuted])

  const hangUp = useCallback(() => {
    hungUpLocallyRef.current = true
    callRef.current?.disconnect()
  }, [])

  const sendDigit = useCallback((digit: string) => {
    callRef.current?.sendDigits(digit)
  }, [])

  return {
    step,
    isMuted,
    ended,
    isStarting,
    qualityAlert,
    startCall,
    mute,
    hangUp,
    sendDigit,
    cleanup,
    reset,
  }
}
